import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Representer, representerPrelevement } from './EcheancesMandat';
import { leverSuspension } from './HistoriqueMandats';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

/** Factures & Reçus ESS : prélèvements SEPA rejetés à représenter (un clic) et représentations programmées. */
export default function RejetsMandatsSEPA() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const charger = useCallback(() => {
    axios.get(`${API}/sepa-stripe/admin/prelevements-rejetes`, { headers: getAuthHeader() })
      .then(({ data: d }) => setData(d)).catch(() => setData({ rejets: [] }));
  }, [getAuthHeader]);
  useEffect(() => { charger(); }, [charger]);

  const liste = data?.rejets || [];
  const aRepresenter = liste.filter((r) => r.statut === 'echec').length;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rejets-mandats-sepa">
      <div className="mb-3 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-rose-600" />
        <h3 className="flex-1 text-base font-bold text-[#0a3d62]">
          Prélèvements SEPA rejetés
          <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700" data-testid="rejets-compteur">{aRepresenter} à représenter</span>
        </h3>
        {data && <span className="text-xs text-slate-500">Frais de rejet : {eur(data.frais_eur)} par échéance, prélevés séparément à la représentation</span>}
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : liste.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun prélèvement rejeté.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr><th className="p-2">Membre</th><th className="p-2">Mandat</th><th className="p-2">Échéance</th><th className="p-2">Montant</th><th className="p-2">Motif</th><th className="p-2">Frais</th><th className="p-2" /></tr>
          </thead>
          <tbody>
            {liste.map((r) => {
              const base = `${API}/sepa-stripe/admin/mandats/${r.mandat_id}`;
              return (
                <tr key={`${r.mandat_id}-${r.numero}`} className="border-t border-slate-100 align-top" data-testid={`rejet-${r.mandat_id}-${r.numero}`}>
                  <td className="p-2"><b>{r.membre_nom}</b><br /><span className="text-xs text-slate-500">{r.raison_sociale}</span>{r.suspendu && <><br /><span className="mt-1 inline-block rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`rejet-suspendu-${r.mandat_id}-${r.numero}`}>Couverture suspendue</span>{' '}<button type="button" onClick={() => leverSuspension(r.mandat_id, getAuthHeader(), charger)} className="mt-1 rounded border border-emerald-400 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-50" data-testid={`rejet-lever-${r.mandat_id}-${r.numero}`}>Lever</button></>}</td>
                  <td className="p-2 text-xs">{r.dispositif} · {r.rum}</td>
                  <td className="p-2 text-xs">n°{r.numero} du {fr(r.date)}{r.tentative > 1 ? <><br />tentative {r.tentative}</> : null}</td>
                  <td className="p-2 font-semibold">{eur(r.montant)}</td>
                  <td className="p-2 text-xs text-rose-700">{r.motif || '—'}</td>
                  <td className="p-2 text-xs">{r.frais ? `${eur(r.frais.montant)} (${r.frais.statut === 'paye' ? 'payés' : 'à prélever'})` : '—'}</td>
                  <td className="p-2 text-right">
                    {r.statut === 'echec' ? (
                      <Representer e={r} fraisEur={r.frais && r.frais.statut !== 'paye' ? r.frais.montant : 0}
                        onValider={(e, jour) => representerPrelevement(base, getAuthHeader(), e, jour, charger)} />
                    ) : (
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800" data-testid={`rejet-programme-${r.mandat_id}-${r.numero}`}>Représentation le {fr(r.date_representation)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
